/**
 * Application Warmup System
 *
 * Handles one-time initialization tasks that run when the app starts.
 * Tasks are fire-and-forget and don't block the app.
 */

import { supabase } from "@/integrations/supabase/client";

interface WarmupTask {
  name: string;
  fn: () => Promise<void>;
  /** If true, only run once ever (uses localStorage). If false, run on every app start. */
  runOnce?: boolean;
}

const WARMUP_STORAGE_KEY = "curio_warmup_completed";

/**
 * Check if a task has already been completed (for runOnce tasks)
 */
function hasTaskCompleted(taskName: string): boolean {
  try {
    const completed = localStorage.getItem(WARMUP_STORAGE_KEY);
    if (!completed) return false;
    const tasks: string[] = JSON.parse(completed);
    return tasks.includes(taskName);
  } catch {
    return false;
  }
}

/**
 * Mark a task as completed
 */
function markTaskCompleted(taskName: string): void {
  try {
    const completed = localStorage.getItem(WARMUP_STORAGE_KEY);
    const tasks: string[] = completed ? JSON.parse(completed) : [];
    if (!tasks.includes(taskName)) {
      tasks.push(taskName);
      localStorage.setItem(WARMUP_STORAGE_KEY, JSON.stringify(tasks));
    }
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Initialize bug reports Google Sheet
 */
async function initBugReportsSheet(): Promise<void> {
  const { error } = await supabase.functions.invoke("init-bug-reports-sheet");
  if (error) {
    console.warn("[Warmup] Bug reports sheet init failed (non-fatal):", error.message);
  } else {
    console.log("[Warmup] Bug reports sheet initialized");
  }
}

/**
 * All warmup tasks to run on app start
 */
const warmupTasks: WarmupTask[] = [
  {
    name: "init-bug-reports-sheet",
    fn: initBugReportsSheet,
    runOnce: false, // Run every startup to ensure headers exist
  },
  // Add more warmup tasks here as needed
  // {
  //   name: 'another-task',
  //   fn: async () => { ... },
  //   runOnce: true,
  // },
];

let warmupStarted = false;

/**
 * Run all warmup tasks
 * Safe to call multiple times - will only run once per app session
 */
export function runAppWarmup(): void {
  if (warmupStarted) return;
  warmupStarted = true;

  console.log("[Warmup] Starting app warmup...");

  for (const task of warmupTasks) {
    // Skip runOnce tasks that have already completed
    if (task.runOnce && hasTaskCompleted(task.name)) {
      console.log(`[Warmup] Skipping "${task.name}" (already completed)`);
      continue;
    }

    // Run task in background (fire and forget)
    task
      .fn()
      .then(() => {
        if (task.runOnce) {
          markTaskCompleted(task.name);
        }
      })
      .catch((err) => {
        console.warn(`[Warmup] Task "${task.name}" failed:`, err);
      });
  }
}

/**
 * Reset warmup state (for testing/admin purposes)
 */
export function resetWarmupState(): void {
  warmupStarted = false;
  localStorage.removeItem(WARMUP_STORAGE_KEY);
}

/**
 * Manually trigger a specific warmup task
 */
export async function runWarmupTask(taskName: string): Promise<void> {
  const task = warmupTasks.find((t) => t.name === taskName);
  if (!task) {
    throw new Error(`Unknown warmup task: ${taskName}`);
  }
  await task.fn();
}
